// Shared vocabulary for the live ops map — the constants, row types and pure
// helpers LiveMapPage, its hooks and its cards all read.
//
// Split out of LiveMapPage.tsx (frontend-standards §10). Every symbol here was
// already in that file; nothing was rewritten on the way out.

import type { LiveTripLeg, LiveTripStatus, OpsLiveTrip, StaffOpsStatusWire } from "@bednbody/types";
import { BOOKING_STATUS_LABEL } from "@/lib/bookingStatusLabels";

export const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

/** Semantic status colors, read from the shadcn theme tokens (packages/ui globals.css). */
export function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export function buildStatusColors(): Record<LiveTripStatus, string> {
  const warning = cssVar("--warning", "#e09729");
  const info = cssVar("--info", "#4599b5");
  const primary = cssVar("--primary", "#009179");
  const success = cssVar("--success", "#45a16b");
  const danger = cssVar("--destructive", "#d15757");
  const muted = cssVar("--muted-foreground", "#6a6c6a");
  return {
    pending: warning,
    awaiting_rider: warning,
    dispatched: info,
    en_route_to_client: primary,
    arrived: primary,
    in_session: success,
    completed: muted,
    cancelled: danger,
  };
}

// `LiveTripStatus` and `booking_status` are the same eight values (see
// `liveTripStatusSchema`) — this IS `BOOKING_STATUS_LABEL`, re-exported under
// the map's existing name so every caller here keeps working unchanged.
export const STATUS_LABELS: Record<LiveTripStatus, string> = BOOKING_STATUS_LABEL;

export const LEG_LABELS: Record<LiveTripLeg, string> = {
  to_pickup: "Rider → hub",
  to_client: "Hub → client",
  waiting: "At client",
  return: "Returning",
  done: "Done",
};

// TERMINAL_LINGER_MS and STALE_MS are gone with the Broadcast reader. They were
// garbage collection for an ephemeral pub/sub stream that could deliver a
// terminal state and then go quiet forever. The snapshot is the truth on every
// poll: a trip that ended is simply absent from the next response, and there is
// nothing left holding a stale dot on the map to expire.

export type Connection = "connecting" | "live" | "error";

/**
 * A live trip with the one thing the snapshot cannot carry: speed.
 *
 * Derived on the client by differencing consecutive fixes, because it is a
 * property of two polls rather than of a row. Null until there IS a previous
 * fix — an invented 0 km/h reads as "stopped", which is a different fact.
 */
export type TrackedLiveTrip = OpsLiveTrip & {
  speedKph: number | null;
  /**
   * Compass bearing of travel, from the previous fix to this one. Null until
   * there are two fixes to draw a line between — the rider icon renders upright
   * rather than inventing a direction, because a scooter pointed the wrong way
   * on an ops map is a worse lie than one pointed north.
   */
  bearing: number | null;
};

/**
 * The scooter marker's id inside the Mapbox style, registered once on load.
 * Shared so the layer and the image registration cannot drift apart.
 */
export const RIDER_ICON_ID = "rider-on-route";

/**
 * ═══ WHAT MARKS A TRIP, BY STATE ═══
 *
 * The rider is DRIVING from the moment he accepts, not only once he is on the
 * client leg: `dispatched` is the ride to the hub to collect the therapist, and
 * an operator watching a scooter appear only halfway through the job has no way
 * to tell "accepted and moving" from "nobody picked it up".
 *
 * At the client the vehicle stops being the story — the session is — so the
 * marker changes shape rather than staying a scooter that never moves again.
 */
export const RIDING_STATUSES: LiveTripStatus[] = ["dispatched", "en_route_to_client"];
export const AT_CLIENT_STATUSES: LiveTripStatus[] = ["arrived", "in_session"];

export function isRiding(status: LiveTripStatus): boolean {
  return RIDING_STATUSES.includes(status);
}

export function isAtClient(status: LiveTripStatus): boolean {
  return AT_CLIENT_STATUSES.includes(status);
}

export type HubMapItem = {
  id: string;
  name: string;
  region: string;
  isPilot: boolean;
  serviceRadiusKm: number;
  point: { lat: number; lng: number };
};

export type ClockedInStaffMapItem = {
  id: string;
  name: string;
  role: "therapist" | "rider";
  hubId: string;
  capturedAt: string;
  locationState: "live" | "home";
  /**
   * TEND-024's six statuses, from `therapist_map_status()` (0104). Null for
   * riders — they have their own lifecycle, and applying a therapist rule to
   * them by assumption is what `never-invent-policy.md` forbids.
   */
  mapStatus: TherapistMapStatus | null;
  /**
   * Bam's per-role status (packages/logic `staff-ops-status.ts`, computed in
   * the staff-location function). `null` against a function deployed before
   * it existed — `live-map.status.ts` falls back to `mapStatus` then.
   */
  opsStatus: StaffOpsStatusWire | null;
  point: { lat: number; lng: number };
};

/** The six approved values, in `0104`'s order. */
export type TherapistMapStatus =
  | "off_session"
  | "active"
  | "pause"
  | "dispatch"
  | "service"
  | "offline";

/**
 * TEND-024: "A map pin displays only Therapist Name and status: Off Session,
 * Active, Pause, Dispatch, Service, or Offline."
 *
 * ⚠ THE LABELS ARE THE RULE'S OWN WORDS, and "only" is the other half of it —
 * the pin carries her name and this, and nothing else. Do not enrich it with a
 * hub, an ETA, a booking or a fix age; TEND-023 already limits who may see the
 * map at all, and widening what the pin says is not this surface's call.
 */
export const THERAPIST_MAP_STATUS_LABELS: Record<TherapistMapStatus, string> = {
  off_session: "Off Session",
  active: "Active",
  pause: "Pause",
  dispatch: "Dispatch",
  service: "Service",
  offline: "Offline",
};

/** Falls back to nothing rather than guessing — a rider has no such status. */
export function therapistMapStatusLabel(status: TherapistMapStatus | null): string | null {
  return status ? THERAPIST_MAP_STATUS_LABELS[status] : null;
}

/**
 * The small status dot a staff pin carries on top of its role color — added
 * 2026-09-23 so a Dispatch/Service/Pause therapist reads differently from an
 * idle one at a glance, without opening every pin. `null` means no dot: the
 * ordinary "Active" state needs no extra flag, and neither does a rider
 * (riders have no `mapStatus` — `LiveMapPage` derives their own dispatch flag
 * from whether they are on a live trip, using this same palette).
 *
 * TEND-024 still governs what a pin's TEXT may say ("only Name and status");
 * this only adds a second, non-textual channel for the same six approved
 * values, so it does not widen what the pin discloses.
 */
export function therapistMapStatusColor(status: TherapistMapStatus | null): string | null {
  if (!status || status === "active") return null;
  const danger = cssVar("--destructive", "#d15757");
  const warning = cssVar("--warning", "#e09729");
  const success = cssVar("--success", "#45a16b");
  const muted = cssVar("--muted-foreground", "#6a6c6a");
  const colors: Record<Exclude<TherapistMapStatus, "active">, string> = {
    dispatch: danger,
    service: success,
    pause: warning,
    off_session: muted,
    offline: muted,
  };
  return colors[status];
}

/**
 * What one pin says: name, and status where there is one.
 *
 * `MapPin` renders a glyph and puts `label` on `aria-label` and `title`, so this
 * IS the pin's text for both a hovering operator and a screen reader. A rider
 * keeps just the name — `mapStatus` is null for them by design.
 */
export function staffPinLabel(staff: {
  name: string;
  mapStatus: TherapistMapStatus | null;
}): string {
  const status = therapistMapStatusLabel(staff.mapStatus);
  return status ? `${staff.name} · ${status}` : staff.name;
}

export function distanceKmBetween(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
): number {
  const earthRadiusKm = 6371;
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const latDelta = toRadians(to.lat - from.lat);
  const lngDelta = toRadians(to.lng - from.lng);
  const a =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(lngDelta / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function speedLabel(speedKph: number | null): string {
  return speedKph === null ? "Waiting for movement" : `${Math.round(speedKph)} km/h`;
}

/**
 * Why staff locations are missing, and what the reader should do about it.
 *
 * ⚠ THE TWO HALVES ARE SEPARATE BECAUSE THEY USED TO BE WELDED TOGETHER. The
 * banner appended "Verify that this account has completed MFA…" to EVERY
 * failure, so a version mismatch between this page and the deployed function —
 * which no amount of signing in can fix — read as an MFA problem. That cost a
 * real debugging session on 2026-08-23. The advice now has to match the fault.
 */
export interface StaffLocationFailure {
  /** What went wrong, from the API envelope where there is one. */
  message: string;
  /** The next action, chosen from the KIND of failure. */
  hint: string;
}

/**
 * The HTTP status behind a Supabase FunctionsHttpError, when there is one.
 *
 * `functionErrorMessage` deliberately unwraps to the envelope's message and
 * drops everything else; this keeps the one fact that separates "you are not
 * allowed" from "the call did not land".
 */
export function functionErrorStatus(error: unknown): number | null {
  return error &&
    typeof error === "object" &&
    "context" in error &&
    error.context instanceof Response
    ? error.context.status
    : null;
}

/** Supabase intentionally keeps non-2xx function errors generic. The response
 * body still holds our API envelope, which gives operations a useful next
 * action (MFA, role, or schema) instead of an opaque transport error. */
export async function functionErrorMessage(error: unknown): Promise<string> {
  if (
    error &&
    typeof error === "object" &&
    "context" in error &&
    error.context instanceof Response
  ) {
    const payload: unknown = await error.context
      .clone()
      .json()
      .catch(() => null);
    if (
      payload &&
      typeof payload === "object" &&
      "error" in payload &&
      payload.error &&
      typeof payload.error === "object" &&
      "message" in payload.error &&
      typeof payload.error.message === "string"
    ) {
      return payload.error.message;
    }
  }
  return error instanceof Error
    ? error.message
    : "The staff-location service could not be reached.";
}

/**
 * How old a rider's last position is, in words.
 *
 * This replaced the ETA the Broadcast payload used to carry. An ETA needs the
 * distance still to run, which needs the client's address — the one thing this
 * surface may never hold. Fix age answers the question an operator actually
 * has: is this dot telling me something, or has the phone gone quiet?
 */
export function fixAgeLabel(recordedAt: string, nowMs: number = Date.now()): string {
  const seconds = Math.round((nowMs - Date.parse(recordedAt)) / 1000);
  if (!Number.isFinite(seconds)) return "—";
  if (seconds < 0) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}

/** One staff member as the live map's detail panel shows them. Read-only. */
export interface StaffDetail {
  id: string;
  name: string;
  roleLabel: string;
  hubName: string;
  statusLabel: string;
  /** e.g. "since 2:14 PM" — the status sheet's "every status carries its time". */
  sinceText: string | null;
  lastContactLabel: string;
  locationLabel: string;
  /** `null` when the caller does not know today's duty. */
  onDutyToday: boolean | null;
  bookingId: string | null;
  bookingRef: string | null;
}
